import { useMemo } from "react";
import {
  geofenceToFeatures,
  getPolygonCentroid,
} from "../utils/geofenceGeometry";
import { isValidCoordinate, pointInPolygon } from "../utils/mapHelpers";
import { logger } from "../utils/logger";

/**
 * Builds the GeoJSON sources for the geofence overlay drawn on the devices map:
 * the polygon source and a centre-pin source annotated with how many of the
 * supplied devices currently fall inside each geofence.
 *
 * @param {Array<Object>} geofences - Geofence records
 * @param {Array<Object>} devices - Devices used for the inside-count
 * @returns {{ geofenceSource: Object, geofencePinSource: Object }}
 */
export function useGeofenceOverlay(geofences, devices) {
  const geofenceFeatures = useMemo(() => {
    if (!geofences || !geofences.length) return [];
    return geofences.flatMap((geofence) => geofenceToFeatures(geofence));
  }, [geofences]);

  const geofenceSource = useMemo(
    () => ({
      type: "FeatureCollection",
      features: geofenceFeatures,
    }),
    [geofenceFeatures]
  );

  // Count how many devices fall inside each geofence
  const geofenceDeviceCounts = useMemo(() => {
    const counts = {};
    if (!geofenceFeatures.length || !devices.length) return counts;

    const byId = {};
    geofenceFeatures.forEach((f) => {
      const id = f.properties.geofence_id;
      if (!byId[id]) byId[id] = [];
      byId[id].push(f.geometry);
    });

    devices.forEach((device) => {
      if (!isValidCoordinate(device?.lng, device?.lat)) return;
      const pt = [parseFloat(device.lng), parseFloat(device.lat)];
      Object.entries(byId).forEach(([id, geometries]) => {
        if (geometries.some((g) => pointInPolygon(pt, g))) {
          counts[id] = (counts[id] || 0) + 1;
        }
      });
    });
    return counts;
  }, [geofenceFeatures, devices]);

  // Centre-pin features with device counts
  const geofencePinFeatures = useMemo(() => {
    return geofenceFeatures
      .map((feature) => {
        try {
          const centroid = getPolygonCentroid(feature.geometry);
          if (!centroid) return null;

          return {
            type: "Feature",
            geometry: { type: "Point", coordinates: centroid },
            properties: {
              ...feature.properties,
              device_count:
                geofenceDeviceCounts[feature.properties.geofence_id] || 0,
            },
          };
        } catch (error) {
          logger.error(
            "Error creating pin for geofence:",
            feature.properties?.geofence_id,
            error
          );
          return null;
        }
      })
      .filter(Boolean);
  }, [geofenceFeatures, geofenceDeviceCounts]);

  const geofencePinSource = useMemo(
    () => ({
      type: "FeatureCollection",
      features: geofencePinFeatures,
    }),
    [geofencePinFeatures]
  );

  return { geofenceSource, geofencePinSource };
}
